"use client";

import { useState } from "react";
import { Check, Copy, Info } from "lucide-react";
import { cn } from "~/lib/utils";

interface CopyFieldProps {
    label: string;
    value: string;
    /** Render the value with a monospace font (good for IDs/secrets). */
    mono?: boolean;
    /** Mask the value behind dots until revealed (for secrets). */
    secret?: boolean;
    /** Optional tooltip text shown via a small info icon next to the reveal button. */
    info?: string;
    className?: string;
    enableCopy?: boolean;
}

export function CopyField({
    label,
    value,
    mono = true,
    secret = false,
    info,
    className,
    enableCopy = true,
}: CopyFieldProps) {
    const [copied, setCopied] = useState(false);
    const [revealed, setRevealed] = useState(!secret);

    async function handleCopy() {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    }

    const display =
        secret && !revealed ? "•".repeat(Math.min(value.length, 32)) : value;

    return (
        <div className={cn("flex flex-col gap-1.5", className)}>
            {(label || info) && (
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                        {label}
                    </span>
                    {info && (
                        <span
                            className="inline-flex text-muted-foreground transition-colors hover:text-foreground"
                            title={info}
                        >
                            <Info className="size-3.5" />
                            <span className="sr-only">{info}</span>
                        </span>
                    )}
                </div>
            )}
            <div className="flex items-stretch gap-2">
                <div
                    className={cn(
                        "flex min-w-0 flex-1 items-center gap-2 rounded-md border bg-muted px-3 py-2 text-sm",
                        mono && "font-mono",
                    )}
                >
                    <span
                        className="min-w-0 flex-1 truncate"
                        title={revealed ? value : undefined}
                    >
                        {display}
                    </span>
                    {secret && (
                        <button
                            type="button"
                            onClick={() => setRevealed((r) => !r)}
                            className="shrink-0 font-sans text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                        >
                            {revealed ? "Hide" : "Reveal"}
                        </button>
                    )}
                </div>
                {enableCopy && (
                    <button
                        type="button"
                        onClick={handleCopy}
                        aria-label={`Copy ${label}`}
                        className={cn(
                            "flex shrink-0 items-center justify-center rounded-md border bg-background px-3 transition-colors hover:bg-muted",
                            copied && "border-primary/40 text-primary",
                        )}
                    >
                        {copied ? (
                            <Check className="size-4" />
                        ) : (
                            <Copy className="size-4" />
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}
